/**
 * Availability Request Page - Trade Loop v2.0
 *
 * Request-scoped route (/availability-requests/:id) — the destination notifications
 * and push messages deep-link to. Before this existed, an availability request could
 * only be reached as a modal on a list page, so a notification had nowhere to point
 * and dumped the user on /dashboard to hunt for it.
 *
 * A request that has already been answered, converted, or expired renders in a
 * read-only terminal state rather than 404ing or bouncing away — the recipient can
 * still see what they were asked and what they decided.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Briefcase, Clock, MapPin, CheckCircle, XCircle } from 'lucide-react';

interface RecipientResponse {
  id: string;
  recipientId: string;
  recipientName: string | null;
  responseStatus: 'pending' | 'available' | 'unavailable' | 'ignored';
  responseMessage: string | null;
  respondedAt: string | null;
}

interface AvailabilityRequestDetail {
  id: string;
  senderId: string;
  senderName: string | null;
  senderCompany: string | null;
  jobDescription: string;
  jobLocation: string;
  requestedAt: string;
  expiresAt: string | null;
  status: string;
  recipients: RecipientResponse[];
}

const isExpired = (expiresAt: string | null): boolean =>
  !!expiresAt && new Date(expiresAt).getTime() < Date.now();

export default function AvailabilityRequestPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [responseMessage, setResponseMessage] = useState('');

  const { data: request, isLoading, error } = useQuery<AvailabilityRequestDetail | null>({
    queryKey: ['/api/workflows/availability-request', id],
    queryFn: async () => {
      const response: any = await api.get(`/api/workflows/availability-request/${id}`);
      return response?.data ?? response ?? null;
    },
    enabled: !!id,
    retry: false,
  });

  const respondMutation = useMutation({
    mutationFn: (status: 'available' | 'unavailable') =>
      api.put(`/api/workflows/availability-request/${id}/respond`, {
        responseStatus: status,
        responseMessage,
      }),
    onSuccess: (_data, status) => {
      showSuccess(
        'Response Submitted',
        `You've marked yourself as ${status === 'available' ? 'Available' : 'Unavailable'}`
      );
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/availability-request', id] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
      queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
    },
    onError: (err: any) => {
      showError('Failed to Submit Response', err?.response?.data?.error || 'Please try again');
    },
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  // 403 and 404 both land here — the user either isn't party to this request or it's gone.
  if (error || !request?.id) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <p className="text-gray-700">
                This availability request isn&apos;t available. It may have been removed, or you may
                not have access to it.
              </p>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const isSender = request.senderId === user?.id;
  const myResponse = request.recipients?.find((r) => r.recipientId === user?.id) || null;

  const expired = isExpired(request.expiresAt);
  const converted = request.status === 'converted';
  const alreadyResponded = !!myResponse && myResponse.responseStatus !== 'pending';

  // Read-only whenever there is nothing left for this viewer to decide.
  const canRespond = !isSender && !!myResponse && !alreadyResponded && !expired && !converted;

  const terminalReason = (() => {
    if (alreadyResponded) {
      const when = myResponse?.respondedAt
        ? ` ${formatDistanceToNow(new Date(myResponse.respondedAt), { addSuffix: true })}`
        : '';
      return myResponse?.responseStatus === 'available'
        ? `You marked yourself available${when}.`
        : `You declined this request${when}.`;
    }
    if (converted) return 'This request was closed — another partner was selected for the job.';
    if (expired) return 'This request expired before you responded.';
    return null;
  })();

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Availability Request</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                From {request.senderName || 'a partner'}
                {request.senderCompany ? ` · ${request.senderCompany}` : ''}
              </p>
            </div>
            {expired && !alreadyResponded ? (
              <Badge variant="secondary">Expired</Badge>
            ) : converted ? (
              <Badge variant="secondary">Closed</Badge>
            ) : alreadyResponded ? (
              <Badge variant={myResponse?.responseStatus === 'available' ? 'default' : 'secondary'}>
                {myResponse?.responseStatus === 'available' ? 'Available' : 'Declined'}
              </Badge>
            ) : (
              <Badge>Pending</Badge>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Briefcase className="h-5 w-5 text-gray-400 shrink-0" />
              <p className="text-gray-800">{request.jobDescription}</p>
            </div>
            <div className="flex gap-2">
              <MapPin className="h-5 w-5 text-gray-400 shrink-0" />
              <p className="text-gray-800">{request.jobLocation}</p>
            </div>
            <div className="flex gap-2">
              <Clock className="h-5 w-5 text-gray-400 shrink-0" />
              <p className="text-gray-800">
                {!request.expiresAt
                  ? 'No expiration'
                  : expired
                    ? `Expired ${formatDistanceToNow(new Date(request.expiresAt), { addSuffix: true })}`
                    : `Expires ${formatDistanceToNow(new Date(request.expiresAt), { addSuffix: true })}`}
              </p>
            </div>

            {myResponse?.responseMessage && (
              <div className="rounded-md bg-gray-50 p-3">
                <p className="text-sm text-gray-500 mb-1">Your message</p>
                <p className="text-gray-800">{myResponse.responseMessage}</p>
              </div>
            )}

            {/* Terminal state — show what happened, offer no actions. */}
            {!isSender && !canRespond && terminalReason && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                <p className="text-gray-700">{terminalReason}</p>
              </div>
            )}

            {canRespond && (
              <div className="space-y-3 pt-2">
                <Textarea
                  placeholder="Add a message (optional)"
                  value={responseMessage}
                  maxLength={100}
                  onChange={(e) => setResponseMessage(e.target.value)}
                />
                <div className="flex gap-3">
                  <Button
                    className="flex-1 gap-2"
                    disabled={respondMutation.isPending}
                    onClick={() => respondMutation.mutate('available')}
                  >
                    <CheckCircle className="h-4 w-4" />
                    I&apos;m Available
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    disabled={respondMutation.isPending}
                    onClick={() => respondMutation.mutate('unavailable')}
                  >
                    <XCircle className="h-4 w-4" />
                    Not Available
                  </Button>
                </div>
              </div>
            )}

            {/* Sender's view: who said what. */}
            {isSender && (
              <div className="pt-2">
                <p className="text-sm font-medium text-gray-700 mb-2">Responses</p>
                <div className="space-y-2">
                  {request.recipients?.length ? (
                    request.recipients.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between rounded-md border border-gray-200 p-3"
                      >
                        <span className="text-gray-800">{r.recipientName || 'Partner'}</span>
                        <Badge variant={r.responseStatus === 'available' ? 'default' : 'secondary'}>
                          {r.responseStatus === 'available'
                            ? 'Available'
                            : r.responseStatus === 'unavailable'
                              ? 'Declined'
                              : 'Pending'}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No recipients.</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
